import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { type MultiPageVideoModel } from '@bynder-studio/render-core';
import { type PlaybackManipulationRenderer } from '@bynder-studio/render-web';
import { IconAspectRatioOutline, IconTime } from '@bynder/icons';
import { colorPalettes } from 'packages/store/brandColors/brandColors.selectors';
import features from 'packages/configs/features';
import AuthorizationHelper from 'packages/helpers/AuthorizationHelper';
import { isCtrlKey } from 'packages/helpers/hotKeys';
import useEditor from 'packages/hooks/useEditor';
import { useIsModalOpen } from 'packages/hooks/useIsModalOpen';
import CanvasZoomControls from 'packages/common/editor/CanvasZoomControls/CanvasZoomControls';
import useEditorTimeframe from 'packages/hooks/useEditorTimeframe';
import MultiPageDropdown from 'packages/pages/editor/MultiPageDropdown';
import useAccessRights from 'packages/hooks/useAccessRights';
import { Account, ActionStatus, ColorPaletteAction } from 'packages/hooks/useAccessRights/types';
import { syncBrandColors } from 'packages/pages/editor/syncBrandColors';
import { SizeLabel } from './Controls.styled';
import usePageShortcuts from 'packages/hooks/shortcuts/usePageShortcuts';

const Controls = ({ template, saved, closePublishModal, save, isInAction, setSaveStatus }) => {
    const {
        creativeModel,
        creativeType,
        exportIntegrations,
        exportConfigurationIds,
        approvalEnabled,
        reviewers,
        manipulationRenderer,
        modalActive,
    } = useEditor<MultiPageVideoModel, PlaybackManipulationRenderer>();
    const { palettes } = useSelector(colorPalettes);
    const isFeatureAvailable = AuthorizationHelper.isFeatureAvailable(features.MULTI_SIZE_CREATIVE);
    const { isAccountActionAllowed } = useAccessRights();
    const { duration, frameRate } = useEditorTimeframe(creativeModel, creativeType);
    const [currentSize, setCurrentSize] = useState(creativeModel.getCurrentPageIndex());
    const totalPages = creativeModel.getModels().length;
    const currentPage = creativeModel.getCurrentPageIndex();
    const isModalOpen = useIsModalOpen();

    const selectedSizeDimension = useMemo(
        () => creativeModel.getModels()?.[currentSize]?.getDimensions(),
        [creativeModel, currentSize],
    );

    const calculateToSecond = duration > 0 && frameRate !== null ? duration / frameRate : 0;

    const enableSave = !isInAction && (!saved || !template.lastPublishDate) && !modalActive;
    const isVideoCreative = creativeType === 'VIDEO';

    const handleCtrlSave = useCallback(
        (e) => {
            if (isCtrlKey(e) && (e.code === 'KeyS' || e.keyCode === 83)) {
                e.preventDefault();

                if (enableSave && !isModalOpen) {
                    save(creativeModel, exportIntegrations, exportConfigurationIds, approvalEnabled, reviewers);
                }
            }
        },
        [
            enableSave,
            isModalOpen,
            creativeModel,
            save,
            exportIntegrations,
            exportConfigurationIds,
            approvalEnabled,
            reviewers,
        ],
    );

    const isElementSelected = useCallback(
        () => (manipulationRenderer?.getSelectedElements() || []).length > 0,
        [manipulationRenderer],
    );

    const handlePageChangePrevious = useCallback(() => {
        if (isElementSelected()) {
            return;
        }

        const prevPage = currentPage - 1 >= 0 ? currentPage - 1 : currentPage;
        creativeModel.setCurrentPageIndex(prevPage);
    }, [creativeModel, currentPage, isElementSelected]);

    const handlePageChangeNext = useCallback(() => {
        if (isElementSelected()) {
            return;
        }

        const nextPage = currentPage + 1 < totalPages ? currentPage + 1 : currentPage;
        creativeModel.setCurrentPageIndex(nextPage);
    }, [creativeModel, currentPage, isElementSelected]);

    usePageShortcuts({ handlePageChangePrevious, handlePageChangeNext });

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        const handleSizeChange = (pageIndex) => setCurrentSize(pageIndex);
        creativeModel?.getEventEmitter()?.on('currentPageChange', handleSizeChange);

        return () => {
            creativeModel?.getEventEmitter()?.off('currentPageChange', handleSizeChange);
        };
    }, [creativeModel]);

    useEffect(() => {
        document.addEventListener('keydown', handleCtrlSave);

        return () => {
            document.removeEventListener('keydown', handleCtrlSave);
        };
    }, [enableSave, isModalOpen]);

    useEffect(() => {
        if (!creativeModel || !saved) {
            return;
        }

        const onCreativeModelAnyChange = () => setSaveStatus(false);
        creativeModel.on('elementUpdated', onCreativeModelAnyChange);
        creativeModel.on('elementRemoved', onCreativeModelAnyChange);
        creativeModel.on('backgroundColorUpdated', onCreativeModelAnyChange);
        creativeModel.on('posterFrameUpdated', onCreativeModelAnyChange);
        creativeModel.on('transcriptUpdated', onCreativeModelAnyChange);
        creativeModel.on('durationUpdated', onCreativeModelAnyChange);
        creativeModel.on('pageAdded', onCreativeModelAnyChange);
        creativeModel.on('pageRenamed', onCreativeModelAnyChange);
        creativeModel.on('pageRemoved', onCreativeModelAnyChange);

        return () => {
            creativeModel.off('elementUpdated', onCreativeModelAnyChange);
            creativeModel.off('elementRemoved', onCreativeModelAnyChange);
            creativeModel.off('backgroundColorUpdated', onCreativeModelAnyChange);
            creativeModel.off('posterFrameUpdated', onCreativeModelAnyChange);
            creativeModel.off('transcriptUpdated', onCreativeModelAnyChange);
            creativeModel.off('durationUpdated', onCreativeModelAnyChange);
            creativeModel.off('pageAdded', onCreativeModelAnyChange);
            creativeModel.off('pageRenamed', onCreativeModelAnyChange);
            creativeModel.off('pageRemoved', onCreativeModelAnyChange);
        };
    }, [creativeModel, saved, setSaveStatus]);

    useEffect(() => {
        if (!isInAction) {
            closePublishModal();
        }
    }, [closePublishModal, isInAction]);

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        if (
            isAccountActionAllowed(ColorPaletteAction.EDIT_COLOR_PALETTE, Account.COLOR_PALETTES) ===
            ActionStatus.ALLOWED
        ) {
            syncBrandColors(creativeModel, palettes, () =>
                save(creativeModel, exportIntegrations, exportConfigurationIds, approvalEnabled, reviewers, true),
            );
        }
    }, [creativeModel, palettes, save, exportIntegrations, exportConfigurationIds, approvalEnabled, reviewers]);

    return (
        <div className="col-auto flex-column d-flex position-relative px-0 zoom-control-wrapper">
            <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                    {isFeatureAvailable && <MultiPageDropdown isInAction={isInAction} />}
                    <SizeLabel className="text-muted">
                        <IconAspectRatioOutline />
                        {`${selectedSizeDimension?.width} × ${selectedSizeDimension?.height}`}
                        {isVideoCreative && (
                            <small className="mx-2">
                                <IconTime size="15" /> {`${calculateToSecond}sec`}
                            </small>
                        )}
                    </SizeLabel>
                </div>

                <div className="d-flex align-items-center editor--controls-right">
                    <CanvasZoomControls renderer={manipulationRenderer} />
                </div>
            </div>
        </div>
    );
};

export default Controls;
